import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@monksflow/monks-ui';
import { RemoveElementButton } from '@/shared/ui/RemoveElementButton';
import { ContentLockCheckbox } from '@/shared/ui/ContentLockCheckbox';
import { buildPublicAssetUrl, markAssetPublic } from '@/shared/api/assets.api';
import { notificationService } from '@/lib/services/notification.service';
import {
  ImageSourcePicker,
  type ImageSource,
  type ImageSourcePickerValue,
  type UseKnowledgeBaseImagesHook,
} from '@/shared/ui/ImageSourcePicker';
import { useTranslation } from 'react-i18next';

export interface ImageEditDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Receives the resolved, absolute `src` to write into the clicked `<img>`, plus whether the user
   * wants the image locked against later agent changes. `locked` is always false when `lock` is
   * omitted (no checkbox shown).
   */
  onConfirm: (src: string, locked: boolean) => void;
  /**
   * Enables the "Lock this image" opt-out. Omit in contexts with no agent to lock against (e.g. the
   * templates-config edit-content mode) and no checkbox is rendered.
   *
   * `checked` is the element's CURRENT lock state; without it the dialog could not tell a lock-only
   * submit from a no-op, so an image could be locked but never unlocked — Save is gated on picking a
   * new image, and the box re-ticked itself on every open.
   */
  lock?: { checked: boolean; disabled: boolean; disabledReason?: string };
  /** The image's current `src`, echoed back when only the lock changed. Required alongside `lock`. */
  currentSrc?: string;
  /** Which picker tabs to offer. Defaults to Upload + Knowledge Base. */
  sources?: ImageSource[];
  /** Dialog heading. Defaults to "Replace Image". */
  title?: string;
  /** Injected kb-image hook (forwarded to the picker). Omitted → kb tab shows no kbs. */
  useKnowledgeBaseImagesHook?: UseKnowledgeBaseImagesHook;
  /** When provided, shows a danger "Remove" action that deletes the whole `<img>` element. */
  onRemove?: () => void;
}

/**
 * Shared image-picker dialog. Backs both the email widget's image edit and the templates-config
 * edit-content mode. Because the chosen image is written straight into HTML that is rendered
 * directly (sanitized `srcDoc` / picker iframe), assets are made public and resolved to an ABSOLUTE
 * `/d/public/{assetId}` URL before being written into `<img src>` — relative / `asset://` values
 * would not render there. URL values are passed through as-is.
 */
export function ImageEditDialog({ open, onClose, ...bodyProps }: ImageEditDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      {/* The body carries every piece of per-opening state and is only mounted while the dialog is
          open, so each opening starts clean. A pick left behind survived Cancel and Save alike, so
          opening a DIFFERENT image and pressing Save purely to lock it wrote the earlier image's
          src onto the new element — with a success toast, and no way back through this dialog. */}
      <DialogContent className="max-w-150" scrollable>
        <ImageEditDialogBody onClose={onClose} {...bodyProps} />
      </DialogContent>
    </Dialog>
  );
}

function ImageEditDialogBody({
  onClose,
  onConfirm,
  sources = ['upload', 'kb'],
  title,
  useKnowledgeBaseImagesHook,
  onRemove,
  lock,
  currentSrc,
}: Omit<ImageEditDialogProps, 'open'>) {
  const { t } = useTranslation('common');
  const heading = title ?? t('components.imageEditDialog.replaceImage');

  const [value, setValue] = useState<ImageSourcePickerValue | null>(null);
  const [saving, setSaving] = useState(false);
  // Mirrors the clicked image's current lock state, like the copy dialog: ticked when it's locked (by
  // the user or the template, here or inherited), unticked when it's free. With no `lock` prop the
  // value is inert (no checkbox rendered), so default to unlocked.
  const [locked, setLocked] = useState(lock ? lock.checked : false);

  // A lock-only change is a valid submission: otherwise unlocking would require replacing the image.
  const lockChanged = !!lock && locked !== lock.checked;
  const canSave = (!!value || lockChanged) && !saving;

  const handleConfirm = async () => {
    if (!value && !lockChanged) return;
    setSaving(true);
    try {
      let src: string;
      if (!value) {
        // Lock-only submit — echo the existing src so nothing about the image changes.
        src = currentSrc ?? '';
      } else if (value.kind === 'url') {
        src = value.url;
      } else {
        await markAssetPublic(value.assetId);
        src = buildPublicAssetUrl(value.assetId);
      }
      onConfirm(src, Boolean(lock) && locked);
    } catch {
      // Most likely `markAssetPublic` failed. Surface it and keep the dialog open so the user can
      // retry, mirroring how PreviewView reports HTML-update failures.
      notificationService.showError(t('components.imageEditDialog.failedToApplyImage'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{heading}</DialogTitle>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        <ImageSourcePicker
          sources={sources}
          onChange={setValue}
          useKnowledgeBaseImagesHook={useKnowledgeBaseImagesHook}
        />
        {lock && (
          <ContentLockCheckbox
            id="image-edit-lock"
            subject="image"
            checked={locked}
            onChange={setLocked}
            disabled={lock.disabled}
            disabledReason={lock.disabledReason}
          />
        )}
      </DialogBody>
      <DialogFooter className={onRemove ? 'sm:justify-between' : undefined}>
        {onRemove && <RemoveElementButton onRemove={onRemove} disabled={saving} />}
        <div className="flex flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('components.imageEditDialog.cancel')}
          </Button>
          <Button onClick={() => void handleConfirm()} disabled={!canSave}>
            {saving && <Spinner aria-hidden />}
            {t('components.imageEditDialog.save')}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
